import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { get } from '@/shared/api/api_client';
import { cn } from '@/shared/utils/cn';
import type { PointBalance } from '@/entities/ledger';

interface BalanceResponse {
  balance: PointBalance;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface PointBalanceBannerProps {
  childId: string;
  familyId: string;
  childName: string;
  /** When true, renders the compact parent version */
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Animated number — smoothly transitions when balance changes
// ---------------------------------------------------------------------------
function AnimatedNumber({ value }: { value: number }): React.ReactElement {
  const [displayed, setDisplayed] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    if (prevRef.current === value) return;

    const start = prevRef.current;
    const end = value;
    const duration = 600;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(start + (end - start) * eased));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        prevRef.current = end;
      }
    };

    requestAnimationFrame(animate);
  }, [value]);

  return <span className="tabular-nums">{displayed.toLocaleString()}</span>;
}

// ---------------------------------------------------------------------------
// PointBalanceBanner component
// ---------------------------------------------------------------------------
export default function PointBalanceBanner({
  childId,
  familyId,
  childName,
  compact = false,
}: PointBalanceBannerProps): React.ReactElement {
  const { data, isLoading } = useQuery<PointBalance>({
    queryKey: ['balance', familyId, childId],
    queryFn: () =>
      get<BalanceResponse>(`/families/${familyId}/balances/${childId}`).then((r) => r.balance),
    enabled: Boolean(familyId && childId),
  });

  // ---- Compact parent view ----
  if (compact) {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 animate-pulse">
          <div className="h-8 w-8 rounded-full bg-slate-200" />
          <div className="space-y-1">
            <div className="h-3 w-20 rounded bg-slate-200" />
            <div className="h-2.5 w-14 rounded bg-slate-200" />
          </div>
        </div>
      );
    }

    return (
      <div
        className="flex items-center gap-2 rounded-lg bg-secondary-50 border border-secondary-200 px-3 py-2"
        aria-label={`${childName}'s balance: ${data?.availablePoints ?? 0} available points`}
      >
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-sm font-bold"
          aria-hidden="true"
        >
          {childName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-800">{childName}</p>
          <p className="flex items-center gap-0.5 text-xs font-medium text-secondary-700">
            <Star size={11} className="text-accent-500" aria-hidden="true" />
            {data ? (
              <AnimatedNumber value={data.availablePoints} />
            ) : (
              <span className="text-slate-400">—</span>
            )}{' '}
            pts
          </p>
        </div>
      </div>
    );
  }

  // ---- Full child view ----
  if (isLoading) {
    return (
      <div
        className="rounded-2xl bg-gradient-to-br from-secondary-500 to-secondary-600 p-6 text-white animate-pulse"
        aria-busy="true"
        aria-label="Loading balance"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="h-16 w-16 rounded-full bg-secondary-400" />
          <div className="h-8 w-36 rounded-xl bg-secondary-400" />
          <div className="h-4 w-28 rounded bg-secondary-400" />
        </div>
      </div>
    );
  }

  const balance = data?.availablePoints ?? 0;
  const reservedPoints = data?.reservedPoints ?? 0;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl p-6 text-white',
        'bg-gradient-to-br from-secondary-500 to-secondary-600',
        'shadow-card-md',
      )}
      role="region"
      aria-label={`Your balance: ${balance} available points`}
    >
      {/* Background decoration */}
      <div
        className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-8 -left-4 h-24 w-24 rounded-full bg-white/5"
        aria-hidden="true"
      />

      <div className="relative flex flex-col items-center gap-2 text-center">
        {/* Star icon */}
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20"
          aria-hidden="true"
        >
          <Star size={28} className="text-accent-300 fill-accent-300" />
        </div>

        {/* Balance number */}
        <div
          className="flex items-baseline gap-1"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="text-5xl font-extrabold tracking-tight">
            <AnimatedNumber value={balance} />
          </span>
        </div>

        {/* Label */}
        <p className="text-sm font-medium text-secondary-100">available points</p>

        {/* Reserved points */}
        {reservedPoints > 0 && (
          <p className="mt-1 rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-secondary-100">
            {reservedPoints.toLocaleString()} pts reserved for pending rewards
          </p>
        )}
      </div>
    </div>
  );
}
